import { useState, useCallback } from 'react';
import { Upload, X, CheckCircle, XCircle, Pause, Play, Trash2, AlertCircle, ImageIcon, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useDirectUpload } from '@/contexts/DirectUploadContext';
import { toast } from 'sonner';
import { DuplicateFilenameDialog } from '../../DuplicateFilenameDialog';
import axios from 'axios';

const UploadPanel = ({
  gallery,
  galleryId,
  photos,
  onUploadComplete,
  axiosConfig,
  backendUrl
}) => {
  const {
    queue,
    addToQueue,
    cancelUpload,
    clearCompleted,
    clearAll,
    pauseUpload,
    resumeUpload,
    totalProgress,
    uploadingCount,
    completedCount,
    failedCount,
    pausedCount
  } = useDirectUpload();

  const [isDragging, setIsDragging] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState('');
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState({ duplicates: [], filesToUpload: [] });
  const [isProcessingDuplicates, setIsProcessingDuplicates] = useState(false);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    await processFiles(files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
      await processFiles(files);
    } else {
      toast.error('Please drop image files only');
    }
  };

  const processFiles = async (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      toast.error('No image files selected');
      return;
    }

    // Fetch fresh list of photo filenames from backend to avoid stale data
    let existingFilenames = [];
    try {
      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/photo-filenames`,
        axiosConfig
      );
      existingFilenames = response.data.filenames || [];
    } catch (err) {
      console.error('Failed to fetch existing filenames, falling back to props:', err);
      // Fall back to photos prop if the API call fails
      existingFilenames = photos?.map(photo => photo.filename) || [];
    }

    // Check for duplicates
    const duplicates = imageFiles.filter(f => existingFilenames.includes(f.name)).map(f => f.name);

    if (duplicates.length > 0) {
      setDuplicateInfo({ duplicates, filesToUpload: imageFiles });
      setShowDuplicateDialog(true);
    } else {
      await startUploads(imageFiles);
    }
  };

  const startUploads = async (files) => {
    console.log(`[UploadPanel] Starting upload of ${files.length} files to gallery ${galleryId}${selectedCollectionId ? ` (collection: ${selectedCollectionId})` : ''}`);
    toast.info(`Starting upload of ${files.length} photo(s)...`);

    // Queue all files in parallel for maximum throughput
    await Promise.all(files.map(file =>
      addToQueue(file, galleryId, selectedCollectionId || null).catch(err => {
        console.error('Failed to queue upload:', err);
        toast.error(`Failed to upload ${file.name}`);
      })
    ));
  };

  const handleSkipDuplicates = async () => {
    setShowDuplicateDialog(false);
    const { duplicates, filesToUpload } = duplicateInfo;
    const nonDuplicates = filesToUpload.filter(f => !duplicates.includes(f.name));

    if (nonDuplicates.length > 0) {
      await startUploads(nonDuplicates);
      toast.info(`Skipped ${duplicates.length} duplicate(s), uploading ${nonDuplicates.length} file(s)`);
    } else {
      toast.info('All selected files are duplicates');
    }
    setDuplicateInfo({ duplicates: [], filesToUpload: [] });
  };

  const handleReplaceAll = async () => {
    const { duplicates, filesToUpload } = duplicateInfo;
    setIsProcessingDuplicates(true);

    try {
      // Delete existing files first
      toast.info(`Deleting ${duplicates.length} existing file(s)...`);

      await axios.delete(
        `${backendUrl}/api/galleries/${galleryId}/photos/by-filename`,
        {
          ...axiosConfig,
          data: { filenames: duplicates }
        }
      );

      toast.success(`Deleted ${duplicates.length} existing file(s)`);

      // Now upload all files
      setShowDuplicateDialog(false);
      await startUploads(filesToUpload);

    } catch (err) {
      console.error('Failed to delete existing files:', err);
      toast.error('Failed to delete existing files: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIsProcessingDuplicates(false);
      setDuplicateInfo({ duplicates: [], filesToUpload: [] });
    }
  };

  const handleAddWithPrefix = async () => {
    const { duplicates, filesToUpload } = duplicateInfo;
    setShowDuplicateDialog(false);

    // Rename duplicate files by adding a number suffix
    const renamedFiles = filesToUpload.map(file => {
      if (duplicates.includes(file.name)) {
        // Generate unique suffix
        const timestamp = Date.now();
        const randomNum = Math.floor(Math.random() * 1000);
        const ext = file.name.lastIndexOf('.') > -1 ? file.name.slice(file.name.lastIndexOf('.')) : '';
        const baseName = file.name.lastIndexOf('.') > -1 ? file.name.slice(0, file.name.lastIndexOf('.')) : file.name;
        const newName = `${baseName}_${timestamp}_${randomNum}${ext}`;

        // Create a new File object with the new name
        return new File([file], newName, { type: file.type });
      }
      return file;
    });

    toast.info(`Renaming ${duplicates.length} file(s) and uploading...`);
    await startUploads(renamedFiles);
    setDuplicateInfo({ duplicates: [], filesToUpload: [] });
  };

  const handleCloseDuplicateDialog = () => {
    if (!isProcessingDuplicates) {
      setShowDuplicateDialog(false);
      setDuplicateInfo({ duplicates: [], filesToUpload: [] });
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-600" />;
      case 'uploading': return <Upload className="w-5 h-5 text-blue-600 animate-pulse" />;
      case 'paused': return <Pause className="w-5 h-5 text-yellow-600" />;
      case 'processing': return <div className="w-5 h-5 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />;
      default: return <Upload className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status, progress) => {
    switch (status) {
      case 'completed': return 'Complete';
      case 'failed': return 'Failed';
      case 'uploading': return `Uploading... ${progress || 0}%`;
      case 'paused': return 'Paused';
      case 'processing': return 'Processing...';
      case 'pending': return 'Waiting...';
      default: return status;
    }
  };

  const pendingCount = queue.filter(q => q.status === 'pending').length;
  const activeCount = uploadingCount + pendingCount + pausedCount;

  return (
    <div className="h-full overflow-auto p-3 md:p-6">
      {/* Duplicate Dialog */}
      <DuplicateFilenameDialog
        isOpen={showDuplicateDialog}
        onClose={handleCloseDuplicateDialog}
        duplicateFiles={duplicateInfo.duplicates || []}
        onSkip={handleSkipDuplicates}
        onReplace={handleReplaceAll}
        onAddWithPrefix={handleAddWithPrefix}
        isProcessing={isProcessingDuplicates}
      />

      {/* Collection Selector */}
      {gallery?.collections?.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-5 h-5 text-slate-500" />
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Upload to:</label>
            <select
              value={selectedCollectionId}
              onChange={(e) => setSelectedCollectionId(e.target.value)}
              className="flex-1 max-w-xs px-3 py-2 border-2 border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 font-medium text-slate-700 dark:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500 focus:outline-none focus:ring-2 focus:ring-[#BF212F] cursor-pointer text-sm"
            >
              <option value="">No Collection (Uncategorized)</option>
              {gallery.collections.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.photo_count || 0} photos)
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Upload Zone */}
      <Card
        className={`mb-4 md:mb-6 border-2 border-dashed transition-all duration-300 cursor-pointer ${
          isDragging
            ? 'border-[#BF212F] bg-red-50 dark:bg-red-900/20 scale-[1.01]'
            : 'border-slate-300 dark:border-slate-600 hover:border-[#BF212F]/50 hover:bg-slate-50'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById('file-upload-input').click()}
      >
        <div className="p-6 md:p-12 text-center">
          <input
            id="file-upload-input"
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />

          {isDragging ? (
            <>
              <Upload className="w-12 md:w-16 h-12 md:h-16 mx-auto mb-4 text-[#BF212F] animate-bounce" />
              <p className="text-lg md:text-xl font-semibold text-[#BF212F] mb-2">Drop your images here</p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-[#BF212F] rounded-2xl mb-4 shadow-lg">
                <Upload className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
              <p className="text-lg md:text-xl font-semibold text-slate-700 dark:text-slate-200 mb-2">
                Drop images here or tap to browse
              </p>
              <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">
                Supports JPG, PNG, HEIC, and other image formats
              </p>
            </>
          )}
        </div>
      </Card>

      {/* Upload Queue */}
      {queue.length > 0 && (
        <Card className="overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 bg-[#BF212F] text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                <span className="font-semibold">Upload Progress</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {completedCount > 0 && <Badge className="bg-green-500">{completedCount} done</Badge>}
                {failedCount > 0 && <Badge className="bg-red-500">{failedCount} failed</Badge>}
                {pausedCount > 0 && <Badge className="bg-yellow-500">{pausedCount} paused</Badge>}
              </div>
            </div>
          </div>

          {/* Overall Progress */}
          <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium text-slate-700 dark:text-slate-300">
                {activeCount} / {queue.length} remaining
              </span>
              <span className="text-slate-600 dark:text-slate-400">{totalProgress}%</span>
            </div>
            <Progress value={totalProgress} className="h-2" />
          </div>

          {/* Action Buttons */}
          <div className="px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b dark:border-slate-700 flex gap-2">
            {activeCount > 0 && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => clearAll()}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel All
              </Button>
            )}
            {completedCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={clearCompleted}
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear Completed
              </Button>
            )}
          </div>

          {/* Queue Items */}
          <div className="max-h-[400px] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-700">
            {queue.map((item) => (
              <div
                key={item.uploadId}
                className={`px-4 py-3 transition-colors ${
                  item.status === 'completed' ? 'bg-green-50 dark:bg-green-900/20' :
                  item.status === 'failed' ? 'bg-red-50 dark:bg-red-900/20' :
                  item.status === 'paused' ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                  ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(item.status)}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-800 dark:text-white truncate">
                        {item.fileName}
                      </span>
                      <span className="text-xs text-slate-500 whitespace-nowrap">
                        {formatFileSize(item.fileSize)}
                      </span>
                    </div>

                    {(item.status === 'uploading' || item.status === 'pending' || item.status === 'processing') && (
                      <div className="mt-2">
                        <Progress value={item.progress || 0} className="h-1.5" />
                        <div className="flex justify-between text-xs text-slate-500 mt-1">
                          <span>{getStatusText(item.status, item.progress)}</span>
                          {item.bytesUploaded && item.bytesTotal && (
                            <span>{formatFileSize(item.bytesUploaded)} / {formatFileSize(item.bytesTotal)}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {item.status === 'failed' && item.error && (
                      <div className="mt-1 text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {item.error}
                      </div>
                    )}

                    {item.status === 'completed' && (
                      <div className="mt-1 text-xs text-green-600">Upload complete</div>
                    )}

                    {item.status === 'paused' && (
                      <div className="mt-1 text-xs text-yellow-600">Paused - click resume to continue</div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {item.status === 'uploading' && (
                      <button
                        onClick={() => pauseUpload(item.uploadId)}
                        className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                        title="Pause"
                      >
                        <Pause className="w-4 h-4 text-yellow-600" />
                      </button>
                    )}
                    {item.status === 'paused' && (
                      <button
                        onClick={() => resumeUpload(item.uploadId)}
                        className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                        title="Resume"
                      >
                        <Play className="w-4 h-4 text-green-600" />
                      </button>
                    )}
                    {(item.status === 'pending' || item.status === 'uploading' || item.status === 'paused' || item.status === 'failed') && (
                      <button
                        onClick={() => cancelUpload(item.uploadId)}
                        className="p-1.5 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                        title="Cancel"
                      >
                        <X className="w-4 h-4 text-slate-500" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Empty State */}
      {queue.length === 0 && (
        <Card className="p-6 md:p-8 text-center border-2 border-dashed border-slate-200 dark:border-slate-700">
          <FolderOpen className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-base md:text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">
            No uploads in progress
          </p>
          <p className="text-xs md:text-sm text-slate-500">
            Drag and drop images above or tap to select files
          </p>
        </Card>
      )}

      {/* Info */}
      <Card className="mt-4 md:mt-6 p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2 text-sm md:text-base">
          Upload Information
        </h4>
        <ul className="text-xs md:text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Photos upload directly to cloud storage for maximum speed</li>
          <li>• Photos are automatically processed with AI tagging</li>
          <li>• Failed uploads retry automatically</li>
          <li className="hidden md:block">• Gallery ID: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded text-xs">{galleryId}</code></li>
        </ul>
      </Card>
    </div>
  );
};

export default UploadPanel;
